import { useState, useEffect, useCallback, useRef } from 'react';
import { Sentry } from '../sentry';
import { trackPromoCodeEvent } from '../utils/gtmTracking';
import { PROMO_CODE_AUTO_APPLY_DELAY, PROMO_SUCCESS_MESSAGE_DELAY } from '../constants';
import { getPromoDescription, isFirstMonthFreePromoCode, MONTHLY_ONLY_PLANS } from '../services/planService';

/**
 * Validates a promo code against Recurly
 * @param {string} code - The promo code to validate
 * @param {string} fullPlanCode - The full plan code including subscription length
 * @returns {Promise<Object>} Validation result
 */
const validatePromoCode = async (code, fullPlanCode) => {
  if (!code || !code.trim()) {
    return {
      isValid: false,
      discount: 0,
      error: 'Please enter a promo code'
    };
  }

  try {
    const recurly = window.recurly;

    // Create a pricing object with the current plan
    const pricing = recurly.Pricing.Checkout();
    const subscription = recurly.Pricing.Subscription();
    await subscription.plan(fullPlanCode);
    await pricing.subscription(subscription);

    // Apply the coupon to see if it's valid and get discount
    await pricing.coupon(code);

    const discountType = pricing.items.coupon.discount.type;
    let discount = 0;
    let discountInfo = null;

    if (discountType === 'free_trial') {
      const discountTrial = pricing.items.coupon.discount.trial;
      discount = discountTrial.amount;
      const discountUnit = discountTrial.unit;
      discountInfo = {
        type: 'trial',
        amount: discount,
        unit: discountUnit
      };
    } else if (discountType === 'percent') {
      // Handle percentage discount
      const rate = pricing.items.coupon.discount.rate;
      discount = parseFloat(rate) * 100; // Convert to percentage (e.g., 0.2 -> 20)
      discountInfo = {
        type: 'percent',
        rate: parseFloat(rate)
      };
    } else {
      // Handle fixed amount discount
      discount = pricing.items.coupon.discount.amount.USD;
      discountInfo = {
        type: 'amount',
        amount: discount
      };
    }

    if (isFirstMonthFreePromoCode(code)) {
      discountInfo = { ...discountInfo, firstMonthFree: true };
    }

    if (parseFloat(discount) > 0) {
      return {
        isValid: true,
        discount: parseFloat(discount),
        discountInfo: discountInfo,
        error: null
      };
    } else {
      return {
        isValid: true,
        discount: 0,
        discountInfo: null,
        error: null
      };
    }
  } catch (error) {
    console.error('Validation error:', error);
    Sentry.captureException(error);
    return {
      isValid: false,
      discount: 0,
      error: 'Unable to validate promo code'
    };
  }
};

/**
 * Helper function to get the full plan code with subscription length
 */
const getFullPlanCode = (plan, subscriptionLength, companyPlans = []) => {
  // Check if this is a company-specific plan (from API)
  const isCompanyPlan = companyPlans.some(p => {
    const code = p.attributes?.code || p.attributes?.['plan-code'];
    return code === plan;
  });

  // Company plans are always monthly-only, or check if in the static monthly-only list
  return (isCompanyPlan || MONTHLY_ONLY_PLANS.includes(plan)) ? plan : plan + '-' + subscriptionLength;
};

/**
 * Builds an applied-promo entry from a validated code, sharing the description
 * logic between the auto-apply and manual-apply paths.
 * @param {string} code - The (already trimmed) promo code
 * @param {Object} validationResult - Result from validatePromoCode
 * @returns {Object} Applied promo code entry
 */
const buildAppliedPromo = (code, validationResult) => ({
  code,
  discount: validationResult.discount,
  discountInfo: validationResult.discountInfo,
  description: getPromoDescription(code, validationResult.discountInfo, validationResult.discount)
});

/**
 * Custom hook to manage promo code functionality
 * @param {Object} options - Hook options
 * @param {string} options.plan - Current plan code
 * @param {string} options.subscriptionLength - Subscription length (monthly/annual)
 * @param {string} options.initialPromoCode - Initial promo code from URL
 * @param {Array} options.companyPlans - Company-specific plans
 * @param {Function} options.getFormContext - Function to get form context for tracking
 * @returns {Object} Promo code state and handlers
 */
export const usePromoCode = ({
  plan,
  subscriptionLength,
  initialPromoCode = '',
  companyPlans = [],
  getFormContext
}) => {
  const [promoCodeValidation, setPromoCodeValidation] = useState({
    isValidating: false,
    isValid: null,
    discount: null,
    error: null
  });
  const [appliedPromoCodes, setAppliedPromoCodes] = useState([]);
  const [currentPromoInput, setCurrentPromoInput] = useState('');
  const [isAutoApplyingPromo, setIsAutoApplyingPromo] = useState(false);
  // Guards the one-time auto-apply. A ref (not state) so the effect doesn't
  // depend on values it mutates — otherwise setting isAutoApplyingPromo would
  // re-run the effect and its cleanup would cancel the pending timer before it
  // fires, so the code from the URL would never actually get applied.
  const hasAutoAppliedRef = useRef(false);

  // Auto-apply promo code(s) from URL parameter (runs once). The `promoCode`
  // param may carry multiple comma-separated codes (e.g. paid-media links that
  // stack a discount coupon with a setup-fee waiver).
  useEffect(() => {
    const seenCodes = new Set();
    const codes = (initialPromoCode || '')
      .split(',')
      .map(code => code.trim())
      .filter(code => {
        if (!code) return false;
        // Dedupe the raw list (case-insensitive) so a repeated code doesn't
        // trigger a redundant Recurly round-trip; keep first-occurrence casing.
        const key = code.toLowerCase();
        if (seenCodes.has(key)) return false;
        seenCodes.add(key);
        return true;
      });

    if (codes.length === 0 || hasAutoAppliedRef.current) {
      return;
    }

    setIsAutoApplyingPromo(true);
    // Populate the input so the "Applying promo code from URL: …" banner shows
    // the code(s) during the auto-apply delay.
    setCurrentPromoInput(codes.join(', '));

    // Delay to ensure Recurly is fully loaded
    const timer = setTimeout(async () => {
      // Mark attempted only once the timer actually fires. If a dependency
      // (plan, subscriptionLength, companyPlans) changes during the delay, the
      // cleanup cancels this timer and the effect re-runs to reschedule a fresh
      // one instead of being permanently blocked.
      hasAutoAppliedRef.current = true;
      try {
        const fullPlanCode = getFullPlanCode(plan, subscriptionLength, companyPlans);

        // Validate each code sequentially. Mirror the manual applyPromoCode
        // path: only apply codes that yield a real discount.
        const appliedThisRun = [];
        // Fallback result used only when nothing gets applied. Prefer the most
        // informative outcome: a valid-but-no-discount code should win over an
        // invalid one so we don't report "Invalid" when a code was actually valid.
        let fallbackValidation = null;
        for (const code of codes) {
          const validationResult = await validatePromoCode(code, fullPlanCode);
          if (validationResult.isValid && validationResult.discount > 0) {
            appliedThisRun.push(buildAppliedPromo(code, validationResult));
          } else if (!fallbackValidation || (validationResult.isValid && !fallbackValidation.isValid)) {
            fallbackValidation = validationResult;
          }
        }

        if (appliedThisRun.length > 0) {
          // Dedupe against the latest applied list — the user may have applied
          // one of the codes manually during the auto-apply delay window.
          setAppliedPromoCodes(prev => {
            const merged = [...prev];
            appliedThisRun.forEach(promo => {
              if (!merged.some(existing => existing.code.toLowerCase() === promo.code.toLowerCase())) {
                merged.push(promo);
              }
            });
            return merged;
          });
          setCurrentPromoInput('');
          const lastApplied = appliedThisRun[appliedThisRun.length - 1];
          setPromoCodeValidation({
            isValidating: false,
            isValid: true,
            discount: lastApplied.discount,
            discountInfo: lastApplied.discountInfo,
            error: null
          });
        } else if (fallbackValidation && fallbackValidation.isValid) {
          setPromoCodeValidation({
            isValidating: false,
            isValid: true,
            discount: 0,
            error: 'Valid code but no discount available for this plan'
          });
        } else {
          setPromoCodeValidation({
            isValidating: false,
            isValid: false,
            discount: null,
            error: (fallbackValidation && fallbackValidation.error) || 'Invalid promo code'
          });
        }
      } catch (error) {
        console.error('Error auto-applying promo code:', error);
        Sentry.captureException(error);
        setPromoCodeValidation({
          isValidating: false,
          isValid: false,
          error: 'Failed to validate promo code'
        });
      } finally {
        setIsAutoApplyingPromo(false);
      }
    }, PROMO_CODE_AUTO_APPLY_DELAY);

    return () => clearTimeout(timer);
  }, [initialPromoCode, plan, subscriptionLength, companyPlans]);

  const applyPromoCode = useCallback(async () => {
    if (!currentPromoInput.trim()) {
      setPromoCodeValidation({
        isValidating: false,
        isValid: false,
        discount: null,
        error: 'Please enter a promo code'
      });
      return;
    }

    // Check if code is already applied
    if (appliedPromoCodes.some(promo => promo.code.toLowerCase() === currentPromoInput.toLowerCase())) {
      const error = 'This promo code has already been applied';
      setPromoCodeValidation({
        isValidating: false,
        isValid: false,
        discount: null,
        error: error
      });
      trackPromoCodeEvent('apply_error', currentPromoInput, { error: 'already_applied' }, getFormContext());
      return;
    }

    setPromoCodeValidation({
      isValidating: true,
      isValid: null,
      discount: null,
      error: null
    });

    // Track promo code application attempt
    trackPromoCodeEvent('apply_attempt', currentPromoInput, null, getFormContext());

    try {
      const fullPlanCode = getFullPlanCode(plan, subscriptionLength, companyPlans);
      const validationResult = await validatePromoCode(currentPromoInput, fullPlanCode);

      if (validationResult.isValid && validationResult.discount > 0) {
        // Validation successful - add the promo code
        const newPromoCode = buildAppliedPromo(currentPromoInput.trim(), validationResult);

        setAppliedPromoCodes(prev => [...prev, newPromoCode]);
        setCurrentPromoInput('');
        setPromoCodeValidation({
          isValidating: false,
          isValid: true,
          discount: validationResult.discount,
          discountInfo: validationResult.discountInfo,
          error: null
        });

        // Track successful promo code application
        trackPromoCodeEvent('apply_success', newPromoCode.code, validationResult, getFormContext());

        // Clear validation state after a brief success message
        setTimeout(() => {
          setPromoCodeValidation({
            isValidating: false,
            isValid: null,
            discount: null,
            error: null
          });
        }, PROMO_SUCCESS_MESSAGE_DELAY);
      } else if (validationResult.isValid && validationResult.discount === 0) {
        const error = 'Valid code but no discount available for this plan';
        setPromoCodeValidation({
          isValidating: false,
          isValid: true,
          discount: 0,
          error: error
        });
        trackPromoCodeEvent('apply_error', currentPromoInput, { error: 'no_discount_available' }, getFormContext());
      } else {
        const error = validationResult.error || 'Invalid promo code';
        setPromoCodeValidation({
          isValidating: false,
          isValid: false,
          discount: null,
          error: error
        });
        trackPromoCodeEvent('apply_error', currentPromoInput, { error: 'invalid_code' }, getFormContext());
      }
    } catch (error) {
      console.error('Apply promo code error:', error);
      Sentry.captureException(error);
      const errorMessage = 'Unable to validate promo code';
      setPromoCodeValidation({
        isValidating: false,
        isValid: false,
        discount: null,
        error: errorMessage
      });
      trackPromoCodeEvent('apply_error', currentPromoInput, { error: 'validation_failed' }, getFormContext());
    }
  }, [currentPromoInput, appliedPromoCodes, plan, subscriptionLength, companyPlans, getFormContext]);

  const removePromoCode = useCallback((codeToRemove) => {
    setAppliedPromoCodes(prev => prev.filter(promo => promo.code !== codeToRemove));
    trackPromoCodeEvent('remove', codeToRemove, null, getFormContext());
  }, [getFormContext]);

  // Calculate discount values
  const calculateTotalDiscount = useCallback((baseCost) => {
    let fixedAmountDiscount = 0;
    let percentageDiscount = 0;

    appliedPromoCodes.forEach(promo => {
      if (promo.discountInfo?.type === 'trial') {
        // Skip trial discounts for monetary calculation
        return;
      } else if (promo.discountInfo?.type === 'percent') {
        // Calculate percentage discount on base cost
        percentageDiscount += baseCost * promo.discountInfo.rate;
      } else if (promo.discountInfo?.type === 'amount') {
        // Add fixed amount discount
        fixedAmountDiscount += promo.discount || 0;
      }
    });

    return fixedAmountDiscount + percentageDiscount;
  }, [appliedPromoCodes]);

  const hasFreeTrial = appliedPromoCodes.some(promo => promo.discountInfo?.type === 'trial');

  return {
    promoCodeValidation,
    appliedPromoCodes,
    currentPromoInput,
    setCurrentPromoInput,
    isAutoApplyingPromo,
    applyPromoCode,
    removePromoCode,
    calculateTotalDiscount,
    hasFreeTrial
  };
};

export default usePromoCode;
